import type { OrderReceiptItemModel } from '~/models/order/OrderReceiptItem.api.model';
import type { PurchaseEvent } from '~/models/tracking/Events.model';
import { tracking_formatPaymentSubTypeForAnalytics } from '~/utils/tracking/paymentSubType';

export const useGoogleAnalyticsEvents = () => {
  const analyticsEventsStore = useAnalyticsEventsStore();

  const pushViewItemEvent = (data: ProductPageTrackingData) => {
    const event = {
      event: 'view_item',
      productPriceId: data.productPriceId,
      downPayTime: data.downPayTime,
      ecommerce: data.ecommerce,
    };

    analyticsEventsStore.pushEvent(event);
  };

  const pushAddToCartEvent = (data: ProductPageTrackingData) => {
    const event = {
      event: 'add_to_cart',
      productPriceId: data.productPriceId,
      downPayTime: data.downPayTime,
      ecommerce: data.ecommerce,
    };

    analyticsEventsStore.pushEvent(event);
  };

  const pushBeginCheckoutEvent = (data: TrackingData) => {
    const event = {
      event: 'begin_checkout',
      ecommerce: data.ecommerce,
    };

    analyticsEventsStore.pushEvent(event);
  };

  const pushAddShippingInfoEvent = (selectedShipping: string) => {
    const event = {
      event: 'add_shipping_info',
      shipping_option: selectedShipping,
    };

    analyticsEventsStore.pushEvent(event);
  };

  const pushNumberOptionEvent = (data: object) => {
    const event = {
      event: 'number_option',
      numberOptions: data,
    };

    analyticsEventsStore.pushEvent(event);
  };

  const pushAddPaymentInfoEvent = (paymentMethodName: string) => {
    const event = {
      event: 'add_payment_info',
      payment_type: paymentMethodName,
    };

    analyticsEventsStore.pushEvent(event);
  };

  const pushStartPaymentEvent = (paymentMethod: string) => {
    // (july 2025)
    // The user can choose a different payment method inside the Klarna
    // widget, so it doesn't make sense to say what Klarna method
    // they selected here
    const formattedPaymentMethod = paymentMethod.includes('Klarna')
      ? 'Klarna'
      : paymentMethod;

    const event = {
      event: 'start_payment',
      payment_type: formattedPaymentMethod,
    };

    analyticsEventsStore.pushEvent(event);
  };

  const pushMenyClickEvent = () => {
    const event = {
      event: 'main_menu_click',
    };
    analyticsEventsStore.pushEvent(event);
  };

  const pushNoMenyClickEvent = () => {
    const event = {
      event: 'no_menu_click',
    };
    analyticsEventsStore.pushEvent(event);
  };

  const pushSubMenyClickEvent = (menuOption: string) => {
    const event = {
      event: 'sub_menu_click',
      menu_option: menuOption,
    };
    analyticsEventsStore.pushEvent(event);
  };

  const pushAddBuybackEvent = (
    condition: string,
    choice: string,
    data: { items: object[] },
    productName: string,
  ) => {
    const event = {
      event: 'add_buyback',
      condition: condition,
      choice: choice,
      productName: productName,
      ecommerce: data,
    };

    analyticsEventsStore.pushEvent(event);
  };

  const pushCheckoutOptionsEvent = (coupon: string) => {
    const event = {
      event: 'checkout_options',
      coupon: coupon,
    };

    analyticsEventsStore.pushEvent(event);
  };

  const pushPurchaseEvent = (
    orderId: number,
    items: OrderReceiptItemModel[],
    coupons: string[],
    value: number,
    paymentType: string,
    shippingOption: string,
    paymentSubType?: string,
    affiliateName?: string,
  ) => {
    const mappedItems = items.flatMap(
      tracking_mapOrderReceiptItemModelToEcommerceItem,
    );

    const event: PurchaseEvent = {
      event: 'purchase',
      ecommerce: {
        items: mappedItems,
        value: value,
        transaction_id: orderId,
        coupon: coupons,
        currency: 'SEK',
        affiliation: affiliateName,
      },
      payment_type: paymentType,
      shipping_option: shippingOption,
    };

    if (paymentSubType) {
      const subType = tracking_formatPaymentSubTypeForAnalytics(paymentSubType);
      event.payment_sub_type = subType;
    }

    analyticsEventsStore.pushEvent(event);
  };

  return {
    pushViewItemEvent,
    pushAddToCartEvent,
    pushBeginCheckoutEvent,
    pushAddShippingInfoEvent,
    pushNumberOptionEvent,
    pushAddPaymentInfoEvent,
    pushStartPaymentEvent,
    pushMenyClickEvent,
    pushNoMenyClickEvent,
    pushSubMenyClickEvent,
    pushAddBuybackEvent,
    pushCheckoutOptionsEvent,
    pushPurchaseEvent,
  };
};
